/**
 * スプリント操作のコア処理
 * useSprintManager と useSprintAdjustment で共通利用される純粋関数群
 */

import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
  isUnsetSprintRowUuid,
  isNotAssociatedWithAnySprint,
  isAssociatedWithTheSprint,
  UNSET_SPRINT_ROW,
  copyProductBacklogItemRowForAssociatedWithMultiSprints,
} from '../productBacklog'

/**
 * 指定されたスプリントの最後の行のインデックスを取得
 */
export const findLastIndexOfSprint = (
  data: ProductBacklogRow[],
  sprintUuid: string
): number => {
  return data.findLastIndex(
    v =>
      v.uuid === sprintUuid ||
      (isProductBacklogItemRow(v) && v.treeValue[0] === sprintUuid)
  )
}

/**
 * 行をスプリントの最後に挿入
 */
export const insertRowsAfterSprint = (
  data: ProductBacklogRow[],
  sprintUuid: string,
  rowsToInsert: ProductBacklogItemRow[]
): ProductBacklogRow[] => {
  if (rowsToInsert.length === 0) return data

  const newData = [...data]
  const lastIndex = findLastIndexOfSprint(newData, sprintUuid)

  if (lastIndex >= 0) {
    newData.splice(lastIndex + 1, 0, ...rowsToInsert)
  } else {
    // スプリント行が見つからない場合は末尾に追加
    newData.push(...rowsToInsert)
  }

  return newData
}

/**
 * 親スプリントとの整合性でフィルタリング
 * - 未設定スプリント配下の行: スプリントに紐付いていない行のみ残す
 * - それ以外: 親スプリントに紐付いている行のみ残す
 */
export const filterBySprintConsistency = (
  data: ProductBacklogRow[]
): ProductBacklogRow[] => {
  return data.filter(row => {
    if (!isProductBacklogItemRow(row)) return true

    const parentSprintRowUuid = row.treeValue[0]

    if (isUnsetSprintRowUuid(parentSprintRowUuid)) {
      return isNotAssociatedWithAnySprint(row)
    }
    return isAssociatedWithTheSprint(row, parentSprintRowUuid)
  })
}

/**
 * スプリント未設定の行を作成
 */
export const createUnsetSprintPbiRow = (
  baseRow: ProductBacklogItemRow
): ProductBacklogItemRow => {
  return {
    ...baseRow,
    uuid: baseRow.originalUuid,
    treeValue: [UNSET_SPRINT_ROW.uuid, baseRow.originalUuid],
    wbsItem: {
      ...baseRow.wbsItem,
      sprints: [],
      sprint: undefined,
    },
    sprintProductItems: [],
  }
}

/**
 * 行を元の位置から削除して、更新済みの行を返す
 */
export const removeRowsFromData = (
  data: ProductBacklogRow[],
  rowUuidsToRemove: Set<string>,
  updateRow?: (row: ProductBacklogItemRow) => ProductBacklogItemRow
): { newData: ProductBacklogRow[]; removedRows: ProductBacklogItemRow[] } => {
  const removedRows: ProductBacklogItemRow[] = []
  const newData = data.filter(row => {
    if (!rowUuidsToRemove.has(row.uuid)) return true
    if (!isProductBacklogItemRow(row)) return true

    const updatedRow = updateRow ? updateRow({ ...row }) : { ...row }
    removedRows.push(updatedRow)
    return false
  })

  return { newData, removedRows }
}

/**
 * 複数スプリント紐付け用のコピー行を作成して挿入
 */
export const insertCopiedRowForMultiSprint = (
  data: ProductBacklogRow[],
  sprintUuid: string,
  baseRow: ProductBacklogItemRow
): {
  newData: ProductBacklogRow[]
  copiedRow: ProductBacklogItemRow | null
} => {
  const lastIndex = findLastIndexOfSprint(data, sprintUuid)

  if (lastIndex < 0) {
    return { newData: data, copiedRow: null }
  }

  const copiedRow = copyProductBacklogItemRowForAssociatedWithMultiSprints(
    sprintUuid,
    baseRow
  )
  const newData = [...data]
  newData.splice(lastIndex + 1, 0, copiedRow)

  return { newData, copiedRow }
}
